import { MultiPolygon, Polygon } from 'geojson';

import { ProcedureModel } from '@/types/config/procedure.types';
import { GlobalScreenConfiguration } from '@/types/config/screenConfiguration.types';
import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { DroneClearanceEnum } from '@/types/utm/drone.types';

import { Abstract, Coordinates, Coordinates3D, Mark, VerticalBounds, WithMargin } from '../commons/commons.types';
import { GridConfiguration, GridLayout } from './gridLayout.types';
import { Home } from './home.types';
import { MergingConfiguration } from './merging.types';
import { PointOfInterest } from './pointOfInterest.types';
import { RoundConfiguration } from './round.types';
import { MapLayer } from './settings.types';
import { NimbleConfiguration } from './videoServer.types';
import { VideoStream } from './videoStream.types';

export enum ZoneTypeEnum {
  NIZ = 'NIZ',
  NFZ = 'NFZ',
  NFZ_3D = 'NFZ_3D',
  ZOI = 'ZOI',
  IMZ = 'IMZ',
  NNZ = 'NNZ',
}

export interface AbstractZone extends Abstract {
  id: number;
  code: string;
  name: string;
  site: string;
  active: boolean;
  marks: Mark[];
}

export interface AbstractZone2d extends AbstractZone {
  polygon: Polygon | Coordinates[];
}

export interface AbstractZone3d extends AbstractZone {
  solidPolygon: MultiPolygon | SolidPolygon3d;
}

export interface SolidPolygon3d {
  polygonFaces: PolygonWithAltitude[];
}

export interface PolygonWithAltitude {
  polygon: Coordinates3D[];
}

export type ZoneWithAltitude = AbstractZone2d & VerticalBounds;

export type ZoneWithMargin = AbstractZone2d & WithMargin;

export interface NoInitZone extends ZoneWithAltitude {
  type: ZoneTypeEnum.NIZ;
}

export interface NoFlyZone extends ZoneWithAltitude {
  type: ZoneTypeEnum.NFZ;
  level: DroneAlertLevelEnum;
  procedureModel: ProcedureModel;
}

export interface NoFlyZone3d extends AbstractZone3d {
  type: ZoneTypeEnum.NFZ_3D;
  level: DroneAlertLevelEnum;
}

export interface ZoneOfInterest extends ZoneWithAltitude {
  type: ZoneTypeEnum.ZOI;
  color: string;
  nature: string;
}

export interface InitializationMaskingZone extends ZoneWithAltitude, ZoneWithMargin {
  type: ZoneTypeEnum.IMZ;
}

export interface NoNeutralizationZone extends AbstractZone2d {
  type: ZoneTypeEnum.NNZ;
}

export type Zone =
  | NoFlyZone
  | InitializationMaskingZone
  | NoInitZone
  | ZoneOfInterest
  | NoNeutralizationZone
  | NoFlyZone3d;

export enum AlertingEventLevelEnum {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export type NfzEntryParameters = {
  enabled: boolean;
  levels: AlertingEventLevelEnum[];
};

export type NfzExitParameters = {
  enabled: boolean;
  levels: AlertingEventLevelEnum[];
  delay: number;
};

export type DroneLostParameters = {
  enabled: boolean;
};

export type MessagingParameters = {
  nfzEntry: NfzEntryParameters;
  nfzExit: NfzExitParameters;
  droneLost: DroneLostParameters;
};

export type Platform = {
  id: number;
  code: string;
  name: string;
  site: string;
  altitude: number | null;
  heightOffset: number;
  polygon: Polygon;
  messagingParameters: MessagingParameters;
};

export type OrganizationParameter = {
  defaultClearance: DroneClearanceEnum;
  enableUtmAlerts: boolean;
  enableLadAlerts: boolean;
  enableTechAlerts: boolean;
  enablePeriAlerts: boolean;
  enableProcedureAutoArchive: boolean;
  expiredNotificationsTtl: number;
  projectionTime: number;
};

export type Organization = {
  id: number;
  code: string;
  name: string;
  site: string;
  email: string;
  platforms: Platform[];
  mapLayers: MapLayer[];
  organizationParameter: OrganizationParameter;
};

export interface ConfigConfiguration {
  home: Home;
  site: string;
  noFlyZones: NoFlyZone[];
  noFlyZones3d: NoFlyZone3d[];
  initializationMaskingZones: InitializationMaskingZone[];
  noInitZones: NoInitZone[];
  zoneOfInterests: ZoneOfInterest[];
  noNeutralizationZones: NoNeutralizationZone[];
  pointOfInterests: PointOfInterest[];
  layouts: GridLayout[];
  gridConfigurations: GridConfiguration[];
  globalScreenConfigurations: GlobalScreenConfiguration[];
  videoStreams: VideoStream[];
  mergingConfiguration: MergingConfiguration;
  videoConfiguration: NimbleConfiguration;
  roundConfigurations: RoundConfiguration[];
  organizations: Organization[];
  platforms: Platform[];
  procedureModels: ProcedureModel[];
}

export enum Zone2DTypeEnum {
  NIZ = 'niz',
  NFZ_INFO = 'observation',
  NFZ_WARNING = 'vigilance',
  NFZ_CRITICAL = 'instantDanger', // Valeurs importantes pour les clés de traductions du formulaire zone
  IMZ = 'imz',
}

export enum Zone3DTypeEnum {
  NFZ_INFO_3D = 'observation3d',
  NFZ_WARNING_3D = 'vigilance3d',
  NFZ_CRITICAL_3D = 'instantDanger3d',
}

export type ZoneFormTypeEnum = Zone2DTypeEnum | Zone3DTypeEnum;

export const zoneCategoryToTypeFormMapper = (zoneCat: ZoneTypeEnum, zoneInfo: DroneAlertLevelEnum | undefined) => {
  switch (zoneCat) {
    case ZoneTypeEnum.NFZ_3D:
      switch (zoneInfo) {
        case DroneAlertLevelEnum.DRONE_INFO:
          return Zone3DTypeEnum.NFZ_INFO_3D;
        case DroneAlertLevelEnum.DRONE_WARNING:
          return Zone3DTypeEnum.NFZ_WARNING_3D;
        case DroneAlertLevelEnum.DRONE_CRITICAL:
          return Zone3DTypeEnum.NFZ_CRITICAL_3D;
        // Shouldn't happen
        case undefined:
          return undefined;
      }
      break;
    case ZoneTypeEnum.NFZ:
      switch (zoneInfo) {
        case DroneAlertLevelEnum.DRONE_INFO:
          return Zone2DTypeEnum.NFZ_INFO;
        case DroneAlertLevelEnum.DRONE_WARNING:
          return Zone2DTypeEnum.NFZ_WARNING;
        case DroneAlertLevelEnum.DRONE_CRITICAL:
          return Zone2DTypeEnum.NFZ_CRITICAL;
        // Shouldn't happen
        case undefined:
          return undefined;
      }
      break;
    case ZoneTypeEnum.IMZ:
      return Zone2DTypeEnum.IMZ;
    case ZoneTypeEnum.NIZ:
      return Zone2DTypeEnum.NIZ;
    //Not implemented yet
    case ZoneTypeEnum.NNZ:
    case ZoneTypeEnum.ZOI:
    default:
      return undefined;
  }
};

export type ZoneToEditType = NoInitZone | NoFlyZone | InitializationMaskingZone;

export const zoneLabelFromZone2DType: Record<Zone2DTypeEnum, string> = {
  [Zone2DTypeEnum.NIZ]: 'radarZone.niz',
  [Zone2DTypeEnum.IMZ]: 'radarZone.imz',
  [Zone2DTypeEnum.NFZ_INFO]: 'dangerZone.observation',
  [Zone2DTypeEnum.NFZ_WARNING]: 'dangerZone.vigilance',
  [Zone2DTypeEnum.NFZ_CRITICAL]: 'dangerZone.instantDanger',
};

export const SEE_POI = 'see_POI';
